/**
 * Agent API Service
 *
 * This service provides methods for interacting with the backend API
 * for agent management operations.
 *
 * Implements the Agent Management API endpoints:
 * - /api/v2/admin/agents/list - List and filter agents with pagination
 * - /api/agents/stats - Get agent statistics and status distribution
 * - /api/agent/register - Register or update an agent
 */

import { get, post, put, del } from './apiClient';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';

// API endpoints - using centralized API_ROUTES constants
const LIST_AGENTS_PATH = API_ROUTES.AGENTS_LIST_ADMIN;
const ADMIN_CREATE_AGENT_PATH = API_ROUTES.AGENT_CREATE;
const AGENT_STATS_PATH = API_ROUTES.AGENT_STATS;
const AGENT_REGISTER_PATH = API_ROUTES.AGENT_REGISTER;
const AGENT_BASE_PATH = API_ROUTES.AGENTS_LIST;
const ADMIN_AGENTS_V2_PATH = API_ROUTES.AGENT_CREATE; // Base path for admin operations

/**
 * Fetch all agents with optional filtering, pagination, and sorting (Admin V2 Endpoint)
 *
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-based, default: 1)
 * @param {number} params.limit - Number of items per page (default: 20, max: 100)
 * @param {string} params.tenant_id - Filter by tenant ID (admin only)
 * @param {string} params.status - Filter by status (idle, busy, error, offline, rebooting)
 * @param {string} params.tags - Comma-separated list of tags to filter by
 * @returns {Promise<Object>} - Response with agents and pagination metadata
 */
export const fetchAgents = async (params = {}) => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    console.log(`Fetching agents from v2 endpoint: ${LIST_AGENTS_PATH}`);
    response = await get(LIST_AGENTS_PATH, { params });
    console.log('Successfully fetched agents from v2 endpoint');
  } catch (error) {
    console.error('Error fetching agents from v2 endpoint:', error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint: ${API_ROUTES.AGENTS_LIST_LEGACY}`
      );

      try {
        // Fall back to legacy endpoint
        // Convert tenant_id to tenant for legacy endpoint if needed
        const legacyParams = { ...params };
        if (legacyParams.tenant_id) {
          legacyParams.tenant = legacyParams.tenant_id;
          delete legacyParams.tenant_id;
        }

        response = await get(API_ROUTES.AGENTS_LIST_LEGACY, { params: legacyParams });
        console.log('Successfully fetched agents from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(
          `Error fetching agents from legacy endpoint ${API_ROUTES.AGENTS_LIST_LEGACY}:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  let agents = [];
  let pagination = {
    total: 0,
    page: parseInt(params.page, 10) || 1,
    limit: parseInt(params.limit, 10) || 20,
    pages: 1,
  };

  if (usedLegacyEndpoint) {
    // Handle legacy response format
    if (Array.isArray(response)) {
      agents = response;
    } else if (response && response.agents) {
      agents = response.agents;
    } else if (response && response.data) {
      agents = Array.isArray(response.data) ? response.data : response.data.agents || [];
    }

    // Set pagination from legacy response if available
    if (response && response.pagination) {
      pagination = response.pagination;
    } else {
      pagination.total = agents.length;
      pagination.pages = Math.ceil(agents.length / pagination.limit);
    }
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch agents');
    }

    agents = response.data?.items || response.agents || [];
    pagination = response.pagination || response.data?.pagination || {
      total: agents.length,
      page: parseInt(params.page, 10) || 1,
      limit: parseInt(params.limit, 10) || 20,
      pages: Math.ceil((agents.length || 1) / (parseInt(params.limit, 10) || 20)),
    };
  }

  return {
    agents: agents.map(agent => transformAgentFromApi(agent)),
    pagination: pagination,
    usedLegacyEndpoint,
  };
};

/** AgentListSchema rejects limit outside 1..100. Page at the maximum instead of sending a larger limit. */
export const AGENT_LIST_PAGE_LIMIT = 100;

export function tenantAgentListParams(tenantId, page = 1) {
  if (!tenantId) {
    throw new Error('Tenant is required');
  }
  return {
    tenant_id: tenantId,
    page,
    limit: AGENT_LIST_PAGE_LIMIT,
  };
}

function agentListPageCount(pagination, receivedCount) {
  const pages = Number(pagination?.pages);
  if (Number.isFinite(pages) && pages >= 1) return pages;
  const total = Number(pagination?.total);
  if (Number.isFinite(total)) {
    return Math.max(1, Math.ceil(total / AGENT_LIST_PAGE_LIMIT));
  }
  if (receivedCount < AGENT_LIST_PAGE_LIMIT) return 1;
  throw new Error('Agent list response did not include pagination');
}

/**
 * Every agent for one tenant. Follows the list endpoint's pagination.
 * Agents for any other tenant are dropped.
 */
export async function fetchRawTenantAgents(tenantId) {
  const collected = [];
  const seen = new Set();
  let page = 1;
  let pages = 1;

  while (page <= pages) {
    const params = tenantAgentListParams(tenantId, page);
    const response = await get(LIST_AGENTS_PATH, { params });
    if (!response?.success) {
      throw new Error(response?.error || response?.message || 'Failed to fetch agents');
    }
    const pageAgents = response.data?.items || response.agents || [];
    const pagination = response.pagination || response.data?.pagination || null;
    pages = agentListPageCount(pagination, pageAgents.length);

    pageAgents.forEach((agent) => {
      const owner = agent?.tenant_id || agent?.tenantId || '';
      if (owner && owner !== tenantId) return;
      const id = agent?.id || agent?.agent_id || agent?.agentId || '';
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      collected.push(agent);
    });

    if (pageAgents.length === 0) break;
    page += 1;
  }

  return collected;
}

export async function fetchAllTenantAgents(tenantId) {
  const collected = (await fetchRawTenantAgents(tenantId)).map(transformAgentFromApi);
  return {
    agents: collected,
    pagination: {
      page: 1,
      limit: AGENT_LIST_PAGE_LIMIT,
      total: collected.length,
      pages: Math.max(1, Math.ceil(collected.length / AGENT_LIST_PAGE_LIMIT)),
    },
  };
}

/**
 * Fetch a single agent by ID
 *
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} - Agent data
 */
export const fetchAgentById = async agentId => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_DETAILS.replace(':agentId', agentId);
    console.log(`Fetching agent details from v2 endpoint: ${url}`);
    response = await get(url);
    console.log('Successfully fetched agent details from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching agent ${agentId} details from v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_DETAILS_LEGACY.replace(':agentId', agentId);
        response = await get(legacyUrl);
        console.log('Successfully fetched agent details from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(`Error fetching agent ${agentId} details from legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  let agentData;

  if (usedLegacyEndpoint) {
    // Handle legacy response format
    if (response && response.agent) {
      agentData = response.agent;
    } else if (response && response.data) {
      agentData = response.data;
    } else {
      agentData = response || {};
    }
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch agent');
    }
    agentData = response.data || {};
  }

  const transformedAgent = transformAgentFromApi(agentData);
  transformedAgent.usedLegacyEndpoint = usedLegacyEndpoint;

  return transformedAgent;
};

/**
 * Persist agent configuration (features and settings) to backend (Firestore)
 *
 * @param {string} agentId - Agent ID
 * @param {Object} config - Configuration payload
 * @param {Object} config.features - Feature toggles
 * @param {Object} config.settings - Agent settings
 * @param {boolean} [config.publish_update=true] - Whether backend should publish MQTT
 * @returns {Promise<Object>} - Response from backend
 */
export const updateAgentConfig = async (
  agentId,
  { features = {}, settings = {}, publish_update = true } = {}
) => {
  try {
    const payload = {
      agent_id: agentId,
      features,
      settings,
      publish_update,
    };

    const url = API_ROUTES.AGENT_CONFIG_UPDATE;
    console.log(`[AgentApi] Persisting agent config via: ${url}`);
    const response = await post(url, payload);

    if (!response.success) {
      throw new Error(
        response.error || response.message || 'Failed to persist agent configuration'
      );
    }

    return response;
  } catch (error) {
    console.error(`Error persisting config for agent ${agentId}:`, error);
    throw error;
  }
};

/**
 * Agent Self-Registration: Register a new agent (typically called by the agent itself).
 * Returns an auth token for the agent.
 *
 * @param {Object} agentData - Agent data for self-registration
 * @returns {Promise<Object>} - Registration response with agent token
 */
export const registerAgent = async agentData => {
  try {
    const apiAgentData = transformAgentToApi(agentData);
    const response = await post(AGENT_REGISTER_PATH, apiAgentData);

    if (!response.token) {
      throw new Error('Failed to register agent: No token received');
    }

    return {
      token: response.token,
      expiresAt: response.expires_at,
    };
  } catch (error) {
    console.error('Error registering agent:', error);
    throw error;
  }
};

/**
 * Update an existing agent
 *
 * @param {string} agentId - Agent ID
 * @param {Object} agentData - Updated agent data
 * @returns {Promise<Object>} - Updated agent
 */
export const updateAgent = async (agentId, agentData) => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const apiAgentData = transformAgentToApi(agentData);
    const url = API_ROUTES.AGENT_UPDATE.replace(':agentId', agentId);
    console.log(`Updating agent using v2 endpoint: ${url}`);
    response = await put(url, apiAgentData);
    console.log('Successfully updated agent using v2 endpoint');
  } catch (error) {
    console.error(`Error updating agent ${agentId} using v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_UPDATE_LEGACY.replace(':agentId', agentId);

        // Legacy API might expect a different format
        const legacyAgentData = {
          ...transformAgentToApi(agentData),
          // Add any legacy-specific fields here if needed
        };

        response = await put(legacyUrl, legacyAgentData);
        console.log('Successfully updated agent using legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(`Error updating agent ${agentId} using legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  let updatedAgent;

  if (usedLegacyEndpoint) {
    // Handle legacy response format
    if (response && response.agent) {
      updatedAgent = response.agent;
    } else if (response && response.data) {
      updatedAgent = response.data;
    } else {
      updatedAgent = response || {};
    }
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to update agent');
    }
    updatedAgent = response.data || {};
  }

  const transformedAgent = transformAgentFromApi(updatedAgent);
  transformedAgent.usedLegacyEndpoint = usedLegacyEndpoint;

  return transformedAgent;
};

/**
 * Delete an agent
 *
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} - Response data
 */
export const deleteAgent = async (agentId, options = {}) => {
  let response;
  let usedLegacyEndpoint = false;
  const { revoke = false } = options;

  try {
    // Try v2 endpoint first
    let url = API_ROUTES.AGENT_DELETE.replace(':agentId', agentId);
    if (revoke) {
      url += (url.includes('?') ? '&' : '?') + 'revoke=true';
    }
    console.log(`Deleting agent using v2 endpoint: ${url} (revoke=${revoke})`);
    response = await del(url);
    console.log('Successfully deleted agent using v2 endpoint');
  } catch (error) {
    console.error(`Error deleting agent ${agentId} using v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_DELETE_LEGACY.replace(':agentId', agentId);
        response = await del(legacyUrl);
        console.log('Successfully deleted agent using legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(`Error deleting agent ${agentId} using legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  if (usedLegacyEndpoint) {
    // Handle legacy response format
    return {
      success: true,
      message: 'Agent deleted successfully',
      usedLegacyEndpoint,
    };
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete agent');
    }

    return {
      success: response.success,
      message: response.message || 'Agent deleted successfully',
      requestId: response.requestId,
      usedLegacyEndpoint,
    };
  }
};

/**
 * Toggle agent status (enable/disable)
 *
 * @param {string} agentId - Agent ID
 * @param {boolean} enabled - Whether to enable or disable
 * @returns {Promise<Object>} - Updated agent status
 */
export const toggleAgentStatus = async (agentId, enabled) => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = `${API_ROUTES.AGENT_UPDATE.replace(':agentId', agentId)}/status`;
    console.log(`Toggling agent status using v2 endpoint: ${url}`);
    response = await put(url, { enabled });
    console.log('Successfully toggled agent status using v2 endpoint');
  } catch (error) {
    console.error(`Error toggling agent ${agentId} status using v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        // For legacy, we might need to use a different approach since there might not be a direct equivalent
        const legacyUrl = API_ROUTES.AGENT_UPDATE_LEGACY.replace(':agentId', agentId);

        // Legacy API might expect a different format or use a different endpoint structure
        response = await put(legacyUrl, {
          status: enabled ? 'enabled' : 'disabled',
          enabled: enabled,
        });
        console.log('Successfully toggled agent status using legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(`Error toggling agent ${agentId} status using legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  if (usedLegacyEndpoint) {
    // Handle legacy response format
    return {
      success: true,
      data: response?.data || {},
      message: `Agent ${enabled ? 'enabled' : 'disabled'} successfully`,
      usedLegacyEndpoint,
    };
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to update agent status');
    }

    return {
      success: response.success,
      data: response.data || {},
      message: response.message || `Agent ${enabled ? 'enabled' : 'disabled'} successfully`,
      requestId: response.requestId,
      usedLegacyEndpoint,
    };
  }
};

/**
 * Restart an agent
 *
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} - Response data
 */
export const restartAgent = async agentId => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_RESTART.replace(':agentId', agentId);
    console.log(`Restarting agent using v2 endpoint: ${url}`);
    response = await post(url);
    console.log('Successfully restarted agent using v2 endpoint');
  } catch (error) {
    console.error(`Error restarting agent ${agentId} using v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_RESTART_LEGACY.replace(':agentId', agentId);
        response = await post(legacyUrl);
        console.log('Successfully restarted agent using legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(`Error restarting agent ${agentId} using legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  if (usedLegacyEndpoint) {
    // Handle legacy response format
    return {
      success: true,
      data: response?.data || {},
      message: 'Agent restart initiated successfully',
      usedLegacyEndpoint,
    };
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to restart agent');
    }

    return {
      success: response.success,
      data: response.data || {},
      message: response.message || 'Agent restart initiated successfully',
      requestId: response.requestId,
      usedLegacyEndpoint,
    };
  }
};

export const fetchAgentActivationCode = async agentId => {
  const url = API_ROUTES.AGENT_ACTIVATION_CODE.replace(':agentId', agentId);
  try {
    const response = await get(url);
    if (!response.success) {
      // Endpoint may be absent until Node install-token issuance ships; do not hard-fail UI.
      return {
        activation_code: '',
        activation_status: 'unavailable',
        message:
          response.message ||
          'Activation codes for desktop agents are not available; use Claim Touch Panel for ESP32 panels.',
      };
    }
    return response.data || {};
  } catch (e) {
    return {
      activation_code: '',
      activation_status: 'unavailable',
      message:
        'Activation-code API not available. For ESP32 Touch Panels use Admin → Touch Panels → Claim Touch Panel.',
    };
  }
};

export const generateAgentActivationCode = async agentId => {
  const url = API_ROUTES.AGENT_ACTIVATION_CODE.replace(':agentId', agentId);
  try {
    const response = await post(url);
    if (!response.success) {
      throw new Error(
        response.message ||
          'Activation-code generation is not available. Use Claim Touch Panel for ESP32 panels.'
      );
    }
    return response.data || {};
  } catch (e) {
    throw new Error(
      e.message ||
        'Activation-code generation is not available. Use Claim Touch Panel for ESP32 panels.'
    );
  }
};

/**
 * Fetch agent statistics and status distribution
 *
 * @param {Object} params - Query parameters
 * @param {string} params.tenant_id - Filter by tenant ID (admin only)
 * @returns {Promise<Object>} - Agent statistics
 */
export const fetchAgentStats = async (params = {}) => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    console.log(`Fetching agent stats from v2 endpoint: ${AGENT_STATS_PATH}`);
    response = await get(AGENT_STATS_PATH, { params });
    console.log('Successfully fetched agent stats from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching agent stats from v2 endpoint ${AGENT_STATS_PATH}:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint: ${API_ROUTES.AGENT_STATS_LEGACY}`
      );

      try {
        // Fall back to legacy endpoint
        // Convert tenant_id to tenant for legacy endpoint if needed
        const legacyParams = { ...params };
        if (legacyParams.tenant_id) {
          legacyParams.tenant = legacyParams.tenant_id;
          delete legacyParams.tenant_id;
        }

        response = await get(API_ROUTES.AGENT_STATS_LEGACY, { params: legacyParams });
        console.log('Successfully fetched agent stats from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(
          `Error fetching agent stats from legacy endpoint ${API_ROUTES.AGENT_STATS_LEGACY}:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  let rawStats = {
    status_counts: {},
    total_agents: 0,
    tenants: [],
    online_percentage: 0,
  };

  if (usedLegacyEndpoint) {
    // Handle legacy response format
    let source = {};
    if (response && response.stats) {
      source = response.stats;
    } else if (response && response.data) {
      source = response.data;
    } else if (response) {
      source = response;
    }

    rawStats.status_counts = source.status_counts || {};
    rawStats.total_agents =
      (typeof source.total_agents === 'number' && source.total_agents) ||
      (typeof source.total === 'number' && source.total) ||
      0;

    const legacyHealth = source.health || {};
    rawStats.online_percentage = legacyHealth.online_percentage || 0;
    rawStats.tenants = source.tenants || [];
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch agent statistics');
    }

    const data = response.data || {};
    rawStats.status_counts = data.status_counts || {};

    const health = data.health_metrics || {};
    if (typeof health.total_agents === 'number') {
      rawStats.total_agents = health.total_agents;
    }
    if (typeof health.online_percentage === 'number') {
      rawStats.online_percentage = health.online_percentage;
    }
    rawStats.tenants = data.tenants || [];
  }

  // Normalize into the shape expected by AgentStatsDashboard
  const total = rawStats.total_agents || 0;

  const defaultStatus = {
    idle: 0,
    busy: 0,
    error: 0,
    offline: 0,
    rebooting: 0,
    unknown: 0,
  };

  const mergedStatus = {
    ...defaultStatus,
    ...(rawStats.status_counts || {}),
  };

  const unhealthyCount = (mergedStatus.error || 0) + (mergedStatus.offline || 0);
  const healthScore =
    total > 0 ? Math.max(0, Math.round(100 * (1 - unhealthyCount / total))) : 0;

  const normalized = {
    total,
    status: mergedStatus,
    online_percentage: rawStats.online_percentage || 0,
    health_score: healthScore,
  };

  return {
    ...normalized,
    tenants: rawStats.tenants || [],
    requestId: response.requestId,
    usedLegacyEndpoint,
  };
};

/**
 * Fetch audit logs for an agent
 *
 * @param {string} agentId - Agent ID
 * @param {Object} params - Pagination parameters
 * @returns {Promise<Object>} - Audit logs with pagination metadata
 */
export const fetchAgentAuditLogs = async (agentId, params = {}) => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_AUDIT.replace(':agentId', agentId);
    console.log(`Fetching audit logs from v2 endpoint: ${url}`);
    response = await get(url, { params });
    console.log('Successfully fetched audit logs from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching audit logs for agent ${agentId} from v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_AUDIT_LEGACY.replace(':agentId', agentId);
        response = await get(legacyUrl, { params });
        console.log('Successfully fetched audit logs from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(
          `Error fetching audit logs for agent ${agentId} from legacy endpoint:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Process the response based on whether we used v2 or legacy endpoint
  let logs = [];
  let pagination = {
    total: 0,
    page: parseInt(params.page, 10) || 1,
    limit: parseInt(params.limit, 10) || 20,
    pages: 0,
  };

  if (usedLegacyEndpoint) {
    // Handle legacy response format
    if (response && Array.isArray(response.logs)) {
      logs = response.logs;
    } else if (response && response.data && Array.isArray(response.data)) {
      logs = response.data;
    } else if (Array.isArray(response)) {
      logs = response;
    }

    // Set pagination from legacy response if available
    if (response && response.pagination) {
      pagination = response.pagination;
    } else if (response && response.meta) {
      pagination.total = response.meta.total || logs.length;
      pagination.page = response.meta.page || parseInt(params.page, 10) || 1;
      pagination.limit = response.meta.limit || parseInt(params.limit, 10) || 20;
      pagination.pages = response.meta.pages || Math.ceil(pagination.total / pagination.limit);
    } else {
      pagination.total = logs.length;
      pagination.pages = Math.ceil(logs.length / pagination.limit);
    }
  } else {
    // Handle v2 API response format
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch agent audit logs');
    }

    logs = response.data?.logs || [];
    pagination = response.data?.pagination || {
      total: logs.length,
      page: parseInt(params.page, 10) || 1,
      limit: parseInt(params.limit, 10) || 20,
      pages: Math.ceil(logs.length / (parseInt(params.limit, 10) || 20)),
    };
  }

  return {
    logs,
    pagination,
    requestId: response.requestId,
    usedLegacyEndpoint,
  };
};

/**
 * Transform API agent data to application format
 *
 * @param {Object} apiAgent - Agent data from API
 * @returns {Object} - Transformed agent data
 */
export const transformAgentFromApi = apiAgent => {
  // Normalize features/capabilities into an array of strings
  const normalizeFeatures = raw => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') return [raw];
    if (typeof raw === 'object') {
      // Common pattern: { sip: true, rest: false }
      return Object.entries(raw)
        .filter(([, enabled]) => !!enabled)
        .map(([key]) => key);
    }
    return [];
  };

  // Handle v2 API response format
  return {
    agentId: apiAgent.id || apiAgent.agent_id,
    agentName: apiAgent.name || apiAgent.device_info?.model || 'Unnamed Agent',
    agentTenant: apiAgent.tenant_id || apiAgent.tenantId,
    siteId:
      apiAgent.siteId ||
      apiAgent.site_id ||
      apiAgent.settings?.siteId ||
      apiAgent.settings?.site_id ||
      apiAgent.config?.siteId ||
      apiAgent.config?.site_id ||
      '',
    status: apiAgent.status || 'unknown',
    healthStatus: apiAgent.healthStatus || apiAgent.health_status,
    lastHeartbeatAt: apiAgent.lastHeartbeatAt || apiAgent.last_heartbeat || apiAgent.last_seen || apiAgent.lastSeen,
    lastSeenSecondsAgo:
      typeof apiAgent.lastSeenSecondsAgo === 'number' ? apiAgent.lastSeenSecondsAgo : null,
    leaseExpiresAt: apiAgent.leaseExpiresAt || null,
    leaseHolder: apiAgent.leaseHolder || null,
    leaseAcquiredAt: apiAgent.leaseAcquiredAt || null,
    enabled:
      apiAgent.enabled !== undefined
        ? apiAgent.enabled
        : apiAgent.status !== 'offline' && apiAgent.status !== 'error',
    lastSeen: apiAgent.last_seen || apiAgent.last_heartbeat || apiAgent.lastSeen,
    lastModified: apiAgent.updated_at || apiAgent.lastModified,
    modifiedBy: apiAgent.modified_by || apiAgent.modifiedBy,
    features: normalizeFeatures(apiAgent.features || apiAgent.capabilities),
    // Preserve panel / ESP32 fields for Touch Panel eligibility (do not drop on transform)
    formFactor: apiAgent.formFactor || apiAgent.form_factor || null,
    form_factor: apiAgent.form_factor || apiAgent.formFactor || null,
    platform: apiAgent.platform || apiAgent.platformType || null,
    touchPanel:
      apiAgent.touchPanel === true ||
      apiAgent.touch_panel === true ||
      (Array.isArray(apiAgent.capabilities) && apiAgent.capabilities.includes('touchPanel')) ||
      apiAgent.capabilityDetails?.touchPanel === true ||
      false,
    // Prefer the string[] capabilities list. capabilityDetails is a richer object used
    // elsewhere — using it here made schema/display eligibility miss panel fields.
    capabilities: Array.isArray(apiAgent.capabilities)
      ? apiAgent.capabilities
      : apiAgent.capabilityDetails || apiAgent.capabilities || null,
    capabilityDetails: apiAgent.capabilityDetails || null,
    display: apiAgent.display || apiAgent.capabilityDetails?.display || null,
    touchPanelSchemaVersions:
      apiAgent.touchPanelSchemaVersions ||
      apiAgent.capabilityDetails?.schemaVersions?.touchPanel ||
      apiAgent.capabilityDetails?.touchPanelSchemaVersions ||
      null,
    maxTouchPanelPayloadBytes:
      apiAgent.maxTouchPanelPayloadBytes ??
      apiAgent.capabilityDetails?.maxTouchPanelPayloadBytes ??
      null,
    firmwareVersion:
      apiAgent.firmwareVersion ||
      apiAgent.firmware_version ||
      apiAgent.device_info?.app_version ||
      null,
    tags: apiAgent.tags || [],
    deviceInfo: apiAgent.device_info || {},
    model: apiAgent.model || apiAgent.device_info?.model || null,
    settings: apiAgent.settings || apiAgent.config || {},
    agentSipId:
      apiAgent.tenant_sip_id ||
      apiAgent.settings?.tenant_sip_id ||
      apiAgent.config?.tenant_sip_id ||
      apiAgent.settings?.sip_id ||
      '',
    agentRestId:
      apiAgent.rest_id || apiAgent.settings?.rest_id || apiAgent.config?.rest_id || '',
    heartbeatInterval:
      apiAgent.heartbeatInterval ||
      apiAgent.settings?.heartbeatInterval ||
      apiAgent.config?.heartbeatInterval ||
      apiAgent.settings?.HEARTBEAT_INTERVAL ||
      apiAgent.config?.HEARTBEAT_INTERVAL ||
      30000,
    // Expose tenant integration fields at top level for UI consumers
    tenant_sip_id:
      apiAgent.tenant_sip_id || apiAgent.settings?.tenant_sip_id || apiAgent.config?.tenant_sip_id,
    rest_id: apiAgent.rest_id || apiAgent.settings?.rest_id || apiAgent.config?.rest_id,
    tenant_id: apiAgent.tenant_id || apiAgent.settings?.tenant_id || apiAgent.config?.tenant_id,
    school_building_name:
      apiAgent.school_building_name ||
      apiAgent.settings?.school_building_name ||
      apiAgent.config?.school_building_name,
    // Track data source for debugging (BE = Backend/Firestore)
    _source: apiAgent._source || 'BE',
  };
};

/**
 * Transform application agent data to API format
 *
 * @param {Object} appAgent - Agent data from application
 * @returns {Object} - Transformed agent data for API
 */
/**
 * Admin Create Agent: Create a new agent as an administrator.
 *
 * @param {Object} agentData - Agent data for creation
 * @returns {Promise<Object>} - Created agent data
 */
export const adminCreateAgent = async agentData => {
  try {
    const apiAgentData = transformAgentToApi(agentData);
    const response = await post(ADMIN_AGENTS_V2_PATH, apiAgentData);

    if (!response || !response.success) {
      throw new Error((response && response.message) || 'Failed to create agent via admin endpoint');
    }

    const createdAgent = response.data || response.agent || {};
    return transformAgentFromApi(createdAgent);
  } catch (error) {
    console.error('Error creating agent via admin endpoint:', error);
    throw error;
  }
};

/**
 * Transform application agent data to API format
 *
 * @param {Object} appAgent - Agent data from application
 * @returns {Object} - Transformed agent data for API
 */
export const transformAgentToApi = appAgent => {
  const deviceInfo = appAgent.deviceInfo || {
    model: appAgent.agentName,
    app_version: '1.0.0',
  };

  const settings = {
    ...(appAgent.settings || appAgent.config || {}),
  };

  if (appAgent.heartbeatInterval !== undefined && appAgent.heartbeatInterval !== null) {
    const parsed = Number(appAgent.heartbeatInterval);
    if (!Number.isNaN(parsed)) {
      settings.heartbeatInterval = parsed;
    }
  }

  const payload = {
    tenantId: appAgent.agentTenant || appAgent.tenant_id || appAgent.tenantId,
    name: appAgent.agentName,
    siteId: appAgent.siteId || appAgent.site_id || settings.siteId || settings.site_id,
    device_info: deviceInfo,
    capabilities: appAgent.features || [],
    tags: appAgent.tags || [],
    // Ensure agent configuration persists by including it in payload
    settings,
    config: settings,
  };

  if (appAgent.agentId && typeof appAgent.agentId === 'string' && appAgent.agentId.trim()) {
    payload.agent_id = appAgent.agentId;
  }

  const sipId =
    appAgent.agentSipId ||
    appAgent.tenant_sip_id ||
    settings.tenant_sip_id ||
    settings.sip_id;
  if (sipId) {
    payload.tenant_sip_id = sipId;
  }

  const restId = appAgent.agentRestId || appAgent.rest_id || settings.rest_id;
  if (restId) {
    payload.rest_id = restId;
  }

  const schoolBuildingName =
    appAgent.school_building_name ||
    settings.school_building_name ||
    settings.school_name ||
    settings.building_name;
  if (schoolBuildingName) {
    payload.school_building_name = schoolBuildingName;
  }

  return payload;
};

/**
 * Revoke an agent (admin only)
 *
 * @param {string} agentId - The ID of the agent to revoke
 * @param {Object} data - Revocation data
 * @param {string} data.reason - Optional reason for revocation
 * @returns {Promise<Object>} - Response with revocation confirmation
 */
export const revokeAgent = async (agentId, data = {}) => {
  const response = await post(`/api/v2/admin/agents/${agentId}/revoke`, data);
  return response.data;
};

/**
 * Get multi-agent context for a tenant (Multi-Agent Operational Rules)
 *
 * @param {string} tenantId - The tenant ID to check for multiple agents
 * @returns {Promise<Object>} - Multi-agent context with warnings and agent list
 */
export const getMultiAgentContext = async (tenantId) => {
  const response = await get(`/api/v2/admin/agents/multi-agent-context/${tenantId}`);
  return response.data;
};

/**
 * Fetch agent status summary with online/offline counts for tenant and global scope.
 * Backend-authoritative: Online = valid lease exists, Offline = lease expired/missing.
 *
 * @returns {Promise<Object>} - { tenant: { online, offline }, global: { online, offline }, timestamp }
 */
export const fetchAgentStatusSummary = async () => {
  const response = await get(API_ROUTES.AGENT_STATUS_SUMMARY, { noDedupe: true });
  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch agent status summary');
  }
  return response.data;
};

export const agentApi = {
  adminCreateAgent, // Add new function to exports
  fetchAgents,
  fetchAgentById,
  registerAgent,
  updateAgent,
  updateAgentConfig,
  deleteAgent,
  toggleAgentStatus,
  restartAgent,
  fetchAgentActivationCode,
  generateAgentActivationCode,
  fetchAgentStats,
  fetchAgentStatusSummary,
  fetchAgentAuditLogs,
  transformAgentFromApi,
  transformAgentToApi,
  revokeAgent,
  getMultiAgentContext,
};

export default agentApi;
